import { useCallback, useRef } from "react";
import { useMediaRecording } from "./useMediaRecording";
import { processCustomAvatarAudio } from "../apis/customAvatar";

const LANGUAGE_MAP: Record<string, string> = {
  ar: "arabic",
  en: "english",
};

export type UseCustomAvatarVoiceParams = {
  sessionId: string | null;
  repeat: (text: string) => void;
  /** Language code ("ar" | "en"). Defaults to "en". */
  language?: string;
};

export type UseCustomAvatarVoiceReturn = {
  isRecording: boolean;
  error: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
};

export const useCustomAvatarVoice = ({
  sessionId,
  repeat,
  language = "en",
}: UseCustomAvatarVoiceParams): UseCustomAvatarVoiceReturn => {
  const repeatRef = useRef(repeat);
  const sessionIdRef = useRef(sessionId);
  const languageRef = useRef(language);

  // Keep refs current without re-creating onSpeechInterval
  repeatRef.current = repeat;
  sessionIdRef.current = sessionId;
  languageRef.current = language;

  const onSpeechInterval = useCallback(async (audioBlob: Blob) => {
    const currentSessionId = sessionIdRef.current;
    if (!currentSessionId) return;

    try {
      const result = await processCustomAvatarAudio(audioBlob, {
        sessionId: currentSessionId,
        language: LANGUAGE_MAP[languageRef.current] ?? "english",
      });

      if (result.response) {
        repeatRef.current(result.response);
      }
    } catch (err) {
      console.error("❌ [CustomAvatarVoice] Failed to process audio:", err);
    }
  }, []);

  const { isRecording, error, start, stop } = useMediaRecording({
    onSpeechInterval,
  });

  const startRecording = useCallback(async () => {
    await start({ audio: true, video: false, silenceTimeoutMs: 1500 });
  }, [start]);

  const stopRecording = useCallback(() => {
    stop();
  }, [stop]);

  return {
    isRecording,
    error,
    startRecording,
    stopRecording,
  };
};
